import { ExclamationCircleFilled } from '@ant-design/icons'
import { faPills, faPlus, faStethoscope, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Modal as AntModal, Button, Card, Col, Modal, Row, Select, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import PrevNextFloatingButtons from '../../../Reusable Components/Buttons/PrevNextFloatingButtons'
import DatePicker from '../../../Reusable Components/DatePicker'
import useChronicIllnesses from '../../hooks/useChronicIllnesses'
import '../style/MobChronicIllnesses.css'
import MobSectionHeader from './MobSectionHeader'

const MobChronicIllnesses = ({ clientForm, data, setFacet, setSaving }) => {
  const { Text } = Typography
  const {
    illnessList,
    handleAddIllness,
    handleRemoveIllness,
    handleUpdateIllness,
    handleRemoveMedication,
    handleAddMedication,
    handleUpdateMedication,
    nappiData,
    icd10Data,
    nappiLoading,
    icd10Loading,
    handleSubmit,
    setICD10SearchFilter,
    setNappiSearchFilter,
    showIllnessModal,
    setShowIllnessModal,
    medicationModalState,
    setMedicationModalState,
    editingIllness,
    setEditingIllness,
    tempDiagnosisDate,
    setTempDiagnosisDate,
    tempMedDates,
    setTempMedDates
  } = useChronicIllnesses({ clientForm, setFacet, setSaving })

  const [showIllnessDatePicker, setShowIllnessDatePicker] = useState(false)
  const [showMedStartDatePicker, setShowMedStartDatePicker] = useState(false)
  const [showMedEndDatePicker, setShowMedEndDatePicker] = useState(false)
  const [validationErrors, setValidationErrors] = useState({
    condition: false,
    diagnosisDate: false
  })

  const styles = {
    header: {
      fontSize: '20px',
      fontWeight: 600,
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    addButton: {
      background: '#00A6E3',
      borderRadius: '8px',
      border: 'none',
      height: '48px',
      fontSize: '16px',
      fontWeight: 500
    },
    illnessCard: {
      borderRadius: '12px',
      border: '1px solid #00A6E3',
      marginBottom: '12px'
    },
    illnessIcon: {
      color: '#00A6E3',
      fontSize: '24px',
      marginTop: '4px'
    },
    illnessName: {
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '16px'
    },
    diagnosisText: {
      color: '#8F9BB3',
      margin: '4px 0 0 0',
      fontSize: '14px'
    },
    addMedicationBtn: {
      border: '1px solid #00A6E3',
      color: '#00A6E3',
      background: 'transparent',
      borderRadius: '8px',
      height: '44px'
    },
    medicationCard: {
      borderRadius: '8px',
      border: '1px solid #52c41a',
      marginBottom: '4px'
    },
    medicationIcon: {
      color: '#00A6E3',
      fontSize: '20px',
      marginTop: '4px'
    },
    medicationName: {
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    removeButton: {
      color: '#FF4D4F',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      background: 'rgba(255, 77, 79, 0.1)',
      border: '1px solid #FF4D4F',
      borderRadius: '4px',
      padding: '4px 8px',
      fontSize: '14px',
      cursor: 'pointer'
    },
    modalContent: {
      color: '#8F9BB3',
      fontSize: '14px',
      lineHeight: '1.5'
    }
  }

  const showDeleteConfirm = (illness, index, handleRemoveIllness) => {
    AntModal.confirm({
      title: 'Remove Chronic Illness',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div style={styles.modalContent}>
          <p>Are you sure you want to remove {illness.description}?</p>
          <p>This will also remove all associated medications:</p>
          <ul>
            {illness.medications?.map((med, idx) => (
              <li key={idx}>{med.med.name}</li>
            ))}
          </ul>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: {
        danger: true
      },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveIllness(index)
      },
      className: 'dark-modal',
      centered: true
    })
  }

  const showDeleteMedicationConfirm = (medication, illnessIndex, medIndex) => {
    AntModal.confirm({
      title: 'Remove Medication',
      icon: <ExclamationCircleFilled style={{ color: '#FF4D4F' }} />,
      content: (
        <div style={styles.modalContent}>
          <p>Are you sure you want to remove {medication.med.name}?</p>
          <p>This action cannot be undone.</p>
        </div>
      ),
      okText: 'Remove',
      okButtonProps: {
        danger: true
      },
      cancelText: 'Cancel',
      onOk() {
        handleRemoveMedication(illnessIndex, medIndex)
      },
      className: 'dark-modal',
      centered: true
    })
  }

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <MobSectionHeader
          icon={faStethoscope}
          title='Chronic Illnesses'
          subtitle='Manage your chronic conditions and medications'
        />
      </Col>
      <Col span={24}>
        <Button
          type='primary'
          onClick={setShowIllnessModal}
          icon={<FontAwesomeIcon icon={faPlus} />}
          block
          style={styles.addButton}
        >
          Add Chronic Illness
        </Button>
      </Col>

      <Col span={24}>
        {illnessList?.length === 0 ? (
          <div className='empty-state' style={{ textAlign: 'center', padding: '40px 20px' }}>
            <FontAwesomeIcon
              icon={faStethoscope}
              className='empty-icon'
              style={{ fontSize: '32px', marginBottom: '12px', opacity: '0.5' }}
            />
            <p style={{ fontSize: '15px', margin: '0' }}>No chronic illnesses added yet</p>
          </div>
        ) : (
          <Space direction='vertical' style={{ width: '100%' }}>
            {illnessList?.map((illness, index) => (
              <Card key={index} style={styles.illnessCard} bodyStyle={{ padding: '16px' }}>
                <Row align='top' justify='space-between'>
                  <Col span={24}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={styles.illnessName}>
                        <div
                          onClick={() => {
                            setEditingIllness({
                              ...illness,
                              index
                            })
                            setTempDiagnosisDate(illness.dateDiagnosed)
                            setShowIllnessModal(true)
                          }}
                          style={{ flex: 1, cursor: 'pointer' }}
                        >
                          {illness.description
                            .split(' ')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(' ')}
                        </div>
                      </div>
                      <Button
                        type='text'
                        icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#FF4D4F' }} />}
                        onClick={(e) => {
                          e.stopPropagation()
                          showDeleteConfirm(illness, index, handleRemoveIllness)
                        }}
                        style={{ padding: '4px', height: 'auto' }}
                      />
                    </div>
                  </Col>
                </Row>

                <Row gutter={[0, 12]} style={{ marginTop: 16 }}>
                  <Col span={24}>
                    <div
                      style={styles.diagnosisText}
                      onClick={() => {
                        setShowIllnessModal(true)
                      }}
                    >
                      Diagnosed: {dayjs(illness.dateDiagnosed).format('DD MMM YYYY')}
                    </div>
                  </Col>
                  <Col span={24}>
                    <Space direction='vertical' style={{ width: '100%' }}>
                      <Button
                        block
                        onClick={() => setMedicationModalState({ show: true, index })}
                        icon={<FontAwesomeIcon icon={faPills} />}
                        style={{
                          ...styles.addMedicationBtn,
                          width: '100%'
                        }}
                      >
                        Add Medication
                      </Button>
                    </Space>
                  </Col>

                  {illness.medications?.length > 0 && (
                    <Col span={24} style={{ marginBottom: 0, paddingBottom: 0 }}>
                      <div style={{ fontWeight: 400, marginTop: '8px', marginBottom: '2px', color: '#8F9BB3' }}>
                        Associated Medications
                      </div>
                    </Col>
                  )}

                  <Col span={24} style={{ marginTop: 0, paddingTop: 0 }}>
                    <Space direction='vertical' style={{ width: '100%' }}>
                      {illness.medications?.map((med, medIndex) => (
                        <Card key={medIndex} style={styles.medicationCard} bodyStyle={{ padding: '12px' }}>
                          <Row gutter={[0, 12]} align='top' justify='space-between'>
                            <Col span={24}>
                              <div
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                              >
                                <div>
                                  <div
                                    style={styles.medicationName}
                                    onClick={() => {
                                      setMedicationModalState({
                                        show: true,
                                        index,
                                        medIndex,
                                        editing: true,
                                        medication: med,
                                        selectedMed: med.med
                                      })
                                      setTempMedDates({
                                        startDate: med.startDate,
                                        endDate: med.endDate
                                      })
                                    }}
                                  >
                                    {medIndex + 1}.{' '}
                                    <Space direction='vertical' size={0}>
                                      <Text strong>
                                        {med.med.name
                                          .split(' ')
                                          .map((word) =>
                                            word.length <= 2
                                              ? word.toUpperCase()
                                              : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                                          )
                                          .join(' ')}
                                      </Text>
                                      <Text type='secondary' style={{ fontSize: '12px' }}>
                                        {[med.med.strengthMetric1, med.med.unitOfMeasure1, med.med.presentationCode]
                                          .filter(Boolean)
                                          .join(' • ')}
                                      </Text>
                                    </Space>
                                  </div>
                                  <div
                                    style={styles.diagnosisText}
                                    onClick={() => {
                                      setMedicationModalState({
                                        show: true,
                                        index,
                                        medIndex,
                                        editing: true,
                                        medication: med,
                                        selectedMed: med.med
                                      })
                                      setTempMedDates({
                                        startDate: med.startDate,
                                        endDate: med.endDate
                                      })
                                    }}
                                  >
                                    <p>Started: {dayjs(med.startDate).format('DD MMM YYYY')}</p>
                                    <p>
                                      {med.endDate && <span>Ended: {dayjs(med.endDate).format('DD MMM YYYY')}</span>}
                                      {!med.endDate && <span> - Current</span>}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  type='text'
                                  icon={<FontAwesomeIcon icon={faTrash} style={{ color: '#FF4D4F' }} />}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    showDeleteMedicationConfirm(med, index, medIndex)
                                  }}
                                  style={{ padding: '4px', height: 'auto' }}
                                />
                              </div>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                    </Space>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        )}
      </Col>

      <Modal
        title={
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontSize: '18px', fontWeight: 'normal' }}>
              {editingIllness ? 'Edit Chronic Illness' : 'Add Chronic Illness'}
            </div>
          </div>
        }
        open={showIllnessModal}
        onCancel={() => {
          setShowIllnessModal(false)
          setEditingIllness(null)
          setTempDiagnosisDate(null)
          setMedicationModalState((prev) => ({ ...prev, selectedIllness: null }))
          setValidationErrors({ condition: false, diagnosisDate: false })
        }}
        footer={
          <Button
            type='primary'
            disabled={!tempDiagnosisDate || (!editingIllness && !medicationModalState.selectedIllness)}
            onClick={() => {
              const errors = {
                condition: !editingIllness && !medicationModalState.selectedIllness,
                diagnosisDate: !tempDiagnosisDate
              }

              setValidationErrors(errors)

              if (!errors.condition && !errors.diagnosisDate) {
                if (editingIllness) {
                  handleUpdateIllness()
                } else {
                  handleAddIllness(medicationModalState.selectedIllness)
                }
              }
            }}
            style={{
              borderRadius: '6px',
              height: '40px',
              fontWeight: 500,
              width: '100%'
            }}
          >
            {editingIllness ? 'Update' : 'Add'} Illness
          </Button>
        }
        className='dark-modal'
        width={340}
        centered
        getContainer={document.body}
      >
        {editingIllness ? (
          <>
            <div style={{ marginBottom: '16px', background: '#f6f8fa', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '16px', fontWeight: 500 }}>
                {editingIllness.description} ({editingIllness.code})
              </div>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 8px 0', color: '#8F9BB3' }}>Find your condition by searching below:</p>
            <ul style={{ margin: '0 0 16px 20px', paddingLeft: '0', color: '#8F9BB3' }}>
              <li>Type the name of your condition or an ICD-10 code</li>
              <li>Choose the matching condition from the list</li>
            </ul>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ color: '#8F9BB3', marginBottom: '8px', fontWeight: 500 }}>Condition</div>
              <Select
                style={{ width: '100%' }}
                showSearch
                placeholder='E.g. Type "Diabetes", "Hypertension", "J45" ...'
                defaultActiveFirstOption={false}
                showArrow={false}
                filterOption={false}
                onSearch={setICD10SearchFilter}
                onSelect={(value) => {
                  const parsedValue = typeof value === 'string' ? JSON.parse(value) : value
                  setMedicationModalState((prev) => ({
                    ...prev,
                    selectedIllness: parsedValue
                  }))
                  setValidationErrors((prev) => ({ ...prev, condition: false }))
                }}
                notFoundContent={icd10Loading ? 'Searching...' : 'No results found. Try different keywords.'}
                options={icd10Data.map((item) => ({
                  value: JSON.stringify(item),
                  label: `${item.description} (${item.code})`
                }))}
                value={
                  medicationModalState.selectedIllness
                    ? JSON.stringify(medicationModalState.selectedIllness)
                    : undefined
                }
                status={validationErrors.condition ? 'error' : ''}
              />
              {validationErrors.condition && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>Please select a condition</div>
              )}
            </div>
          </>
        )}

        <div style={{ marginTop: '16px' }}>
          <div style={{ color: '#8F9BB3', marginBottom: '8px', fontWeight: 500 }}>Diagnosis Date</div>
          <div
            onClick={() => setShowIllnessDatePicker(true)}
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              cursor: 'pointer',
              border: `1px solid ${validationErrors.diagnosisDate ? '#ff4d4f' : '#d9d9d9'}`,
              background: '#fff',
              color: tempDiagnosisDate ? '#000000' : '#8F9BB3'
            }}
          >
            {tempDiagnosisDate
              ? dayjs(tempDiagnosisDate).format('DD MMM YYYY')
              : editingIllness?.dateDiagnosed
              ? dayjs(editingIllness.dateDiagnosed).format('DD MMM YYYY')
              : 'Select diagnosis date'}
          </div>
          {validationErrors.diagnosisDate && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>Please select a diagnosis date</div>
          )}
        </div>

        <DatePicker
          showDatePicker={showIllnessDatePicker}
          setShowDatePicker={setShowIllnessDatePicker}
          pickerValue={
            tempDiagnosisDate
              ? dayjs(tempDiagnosisDate)
              : editingIllness?.dateDiagnosed
              ? dayjs(editingIllness.dateDiagnosed)
              : null
          }
          setPickerValue={(date) => {
            setTempDiagnosisDate(date)
            setValidationErrors((prev) => ({ ...prev, diagnosisDate: false }))
          }}
          handleDateSelect={(date) => {
            setTempDiagnosisDate(date)
            setValidationErrors((prev) => ({ ...prev, diagnosisDate: false }))
            setShowIllnessDatePicker(false)
          }}
        />
      </Modal>

      <Modal
        title={
          <div style={{ padding: '4px 0' }}>
            <div style={{ fontSize: '18px', fontWeight: 'normal' }}>
              {medicationModalState.editing ? 'Edit Medication' : 'Add Medication'}
            </div>
          </div>
        }
        open={medicationModalState.show}
        onCancel={() => {
          setMedicationModalState({ show: false, index: 0 })
          setTempMedDates({ startDate: null, endDate: null })
        }}
        footer={
          <Button
            type='primary'
            disabled={!tempMedDates.startDate || (!medicationModalState.editing && !medicationModalState.selectedMed)}
            onClick={() => {
              if (medicationModalState.editing) {
                handleUpdateMedication(medicationModalState.index, medicationModalState.medIndex, tempMedDates)
              } else {
                handleAddMedication(medicationModalState.selectedMed, tempMedDates)
              }
              setMedicationModalState({ show: false, index: 0 })
              setTempMedDates({ startDate: null, endDate: null })
            }}
            style={{
              borderRadius: '6px',
              height: '40px',
              fontWeight: 500,
              width: '100%'
            }}
          >
            {medicationModalState.editing ? 'Update' : 'Add'} Medication
          </Button>
        }
        className='dark-modal'
        width={340}
        centered
        getContainer={document.body}
      >
        {medicationModalState.editing ? (
          <>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '16px', fontWeight: 500 }}>
                {medicationModalState.selectedMed.name}
                {medicationModalState.selectedMed.dosageForm && ` (${medicationModalState.selectedMed.dosageForm})`}
              </div>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 8px 0', color: '#8F9BB3' }}>Find your medication by searching below:</p>
            <ul style={{ margin: '0 0 16px 20px', paddingLeft: '0', color: '#8F9BB3' }}>
              <li>Type the medication name or active ingredient</li>
              <li>Choose the matching medication from the list</li>
            </ul>
            <Select
              style={{ width: '100%' }}
              showSearch
              placeholder='E.g. Type "Metformin", "Ventolin", "Aspirin" ...'
              defaultActiveFirstOption={false}
              filterOption={false}
              onSearch={setNappiSearchFilter}
              onSelect={(value) => {
                const parsedValue = typeof value === 'string' ? JSON.parse(value) : value
                setMedicationModalState((prev) => ({
                  ...prev,
                  selectedMed: parsedValue
                }))
              }}
              notFoundContent={nappiLoading ? 'Searching...' : 'No results found. Try different keywords or spelling.'}
              options={nappiData.map((medData) => ({
                search: `${medData.name || ''} ${medData.strengthMetric1 || ''} ${medData.unitOfMeasure1 || ''} ${
                  medData.presentationCode || ''
                } - ${medData.standardPacksize || ''}`,
                label: (
                  <Space direction='vertical' size={0}>
                    <Text strong>{medData.name || ''}</Text>
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      {[medData.strengthMetric1, medData.unitOfMeasure1, medData.presentationCode]
                        .filter(Boolean)
                        .join(' • ')}
                    </Text>
                  </Space>
                ),
                value: JSON.stringify({
                  code: medData.code,
                  name: medData.name
                })
              }))}
              value={medicationModalState.selectedMed ? JSON.stringify(medicationModalState.selectedMed) : undefined}
              disabled={medicationModalState.editing}
            />
          </>
        )}
        <div style={{ marginTop: '16px' }}>
          <div style={{ color: '#8F9BB3', marginBottom: '8px' }}>Start Date</div>
          <div
            onClick={() => setShowMedStartDatePicker(true)}
            style={{
              border: '1px solid #8F9BB3',
              padding: '8px 12px',
              borderRadius: '8px',
              color: tempMedDates.startDate ? '#000000' : '#8F9BB3',
              cursor: 'pointer'
            }}
          >
            {tempMedDates.startDate ? dayjs(tempMedDates.startDate).format('DD MMM YYYY') : 'Select start date'}
          </div>
        </div>
        <div style={{ marginTop: '16px' }}>
          <div style={{ color: '#8F9BB3', marginBottom: '8px' }}>End Date (Optional)</div>
          <div
            onClick={() => setShowMedEndDatePicker(true)}
            style={{
              border: '1px solid #8F9BB3',
              padding: '8px 12px',
              borderRadius: '8px',
              color: tempMedDates.endDate ? '#000000' : '#8F9BB3',
              cursor: 'pointer'
            }}
          >
            {tempMedDates.endDate ? dayjs(tempMedDates.endDate).format('DD MMM YYYY') : 'Select end date'}
          </div>
        </div>

        <DatePicker
          showDatePicker={showMedStartDatePicker}
          setShowDatePicker={setShowMedStartDatePicker}
          pickerValue={tempMedDates.startDate ? dayjs(tempMedDates.startDate) : null}
          setPickerValue={(date) => setTempMedDates((prev) => ({ ...prev, startDate: date }))}
          handleDateSelect={(date) => {
            setTempMedDates((prev) => ({ ...prev, startDate: date }))
            setShowMedStartDatePicker(false)
          }}
        />

        <DatePicker
          showDatePicker={showMedEndDatePicker}
          setShowDatePicker={setShowMedEndDatePicker}
          pickerValue={tempMedDates.endDate ? dayjs(tempMedDates.endDate) : null}
          setPickerValue={(date) => setTempMedDates((prev) => ({ ...prev, endDate: date }))}
          handleDateSelect={(date) => {
            setTempMedDates((prev) => ({ ...prev, endDate: date }))
            setShowMedEndDatePicker(false)
          }}
        />
      </Modal>

      <PrevNextFloatingButtons
        onPrev={() => {
          clientForm.resetFields()
          setFacet(null)
          window.scrollTo(0, 0)
        }}
        onNext={handleSubmit}
        nextText='Save'
        prevText='Back'
      />
    </Row>
  )
}

export default MobChronicIllnesses
